<!--
  Created 14.10.2023
  Media component for displaying images and videos.
  Can be used as a carousel or as a grid.
-->
<template>
  <div v-if="mediaItems.length > 1">
    <div
      v-if="isCarousel"
      :class="templateCarouselClass">
      <Carousel
        ref="myCarousel"
        :wrap-around="true"
        :breakpoints="breakpointsSettings"
        class="carousel-media"
        :class="[carouselClass, dynamicClass]"
        snap-align="start">
        <Slide
          v-for="slide in mediaItems"
          :key="'slide-'+slide.id">
          <div class="carousel__item">
            <MediaFigure
              :image="slide"
              :image-alt="slide.custom_properties?.alt_description"
              :hide-fig-caption="hideFigCaption"
              :fig-caption="slide.name"
              :class="mediaFigureClass"
              :has-large-height-mobile="hasLargeHeightMobile"
              :image-classes="isRounded ? ((carouselBleed === 'max-sm:full' || 'max-sm:full md:right') ? 'sm:rounded-md' : 'rounded-md') : ''" />
          </div>
        </Slide>
        <template #addons>
          <Pagination />
          <Navigation />
        </template>
      </Carousel>
    </div>
    <div
      v-else
      class="grid gap-8"
      :class="gridColumnClass">
      <slot name="media">
        <MediaFigure
          v-for="media in mediaItems"
          :key="media.id"
          :image="media"
          :image-alt="media.custom_properties?.alt_description"
          :hide-fig-caption="hideFigCaption"
          :fig-caption="media.name"
          :class="mediaFigureClass"
          :has-large-height-mobile="hasLargeHeightMobile"
          :image-classes="{'rounded-md':isRounded}" />
      </slot>
    </div>
  </div>
  <MediaFigure
    v-else
    :image="mediaItems[0]"
    :image-alt="mediaItems[0]?.custom_properties?.alt_description"
    :hide-fig-caption="hideFigCaption"
    :fig-caption="mediaItems[0]?.name"
    :fig-caption-outside="figCaptionOutside"
    :class="mediaFigureClass"
    :has-large-height-mobile="hasLargeHeightMobile"
    :image-classes="[{'rounded-md':isRounded}, imageClasses]" />
</template>
<script setup>
/**
 * @component
 * Entity Cards component.
 *
 * @param {Array} entities - An array of entity objects. See Entity component for more information on the object structure.
 * @param {number} gridColumn - The number of columns to display.
 *
 * @returns {object}
 *
 * @example
 * <EntityCards
 *  :entities="arrayOfEntities"
 *  />
 */

/* external */
import {
  Carousel, Navigation, Pagination, Slide,
} from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import {
  computed, ref,
} from 'vue';
import MediaFigure from '@/Pages/Proposal/Components/MediaFigure.vue';

/* local */

/**
 * Component Props
 * @typedef {object} Props
 * @property {Array} mediaItems - An array of media objects. See MediaFigure component for more information on the object structure.
 * @property {boolean} isRounded - Whether to display the images as rounded.
 * @property {boolean} figCaptionOutside - Whether to display the figcaption outside the image.
 * @property {boolean} isCarousel - Whether to display the images as a carousel.
 * @property {object} carouselBreakPoints - The number of items to display at each breakpoint, accepts tailwind classes, e.g. sm:2, md:3.25, etc.
 * @property {string} carouselBleed - The bleed of the carousel.
 * @property {string} gridColumnClass - The number of columns to display, accepts tailwind classes, e.g. grid-cols-1, grid-cols-2, etc.
 * @property {string} imageClasses - The classes to apply to the image.
 * @property {string} mediaFigureClass - The classes to apply to the media figure.
 */
const props = defineProps({
  mediaItems: {
    type: Array,
    required: true,
  },
  isRounded: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideFigCaption: {
    type: Boolean,
    required: false,
    default: false,
  },
  figCaptionOutside: {
    type: Boolean,
    required: false,
    default: false,
  },
  isCarousel: {
    type: Boolean,
    required: false,
    default: false,
  },
  carouselBreakPoints: {
    type: Object,
    required: false,
    default: () => ({
      default: null,
      sm: null,
      md: null,
      lg: null,
      xl: null,
      '2xl': null,
    }),
  },
  carouselBleed: {
    type: String,
    required: false,
    default: '',
    validator: (value) => ['', 'max-sm:full', 'max-sm:left', 'max-sm:right', 'full', 'left', 'right', 'max-sm:full sm:right'].includes(value),
  },
  gridColumnClass: {
    type: String,
    default: 'grid-cols-1',
  },
  imageClasses: {
    type: [String, Object, Array],
    required: false,
    default: '',
  },
  mediaFigureClass: {
    type: [String, Object, Array],
    required: false,
    default: '',
  },
  hasLargeHeightMobile: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const myCarousel = ref(null);

const carouselBleedMap = {
  'max-sm:full': {
    templateCarousel: 'max-sm:-mx-6',
    carousel: 'max-sm:mx-break-out',
  },
  'max-sm:left': {
    templateCarousel: 'max-sm:-ml-6',
    carousel: 'max-sm:ml-break-out',
  },
  'max-sm:right': {
    templateCarousel: 'max-sm:-mr-6',
    carousel: 'max-sm:mr-break-out',
  },
  full: {
    templateCarousel: 'sm:-mx-6',
    carousel: 'sm:mx-break-out',
  },
  left: {
    templateCarousel: 'sm:-ml-6',
    carousel: 'sm:ml-break-out',
  },
  right: {
    templateCarousel: 'sm:-mr-6',
    carousel: 'sm:mr-break-out',
  },
  'max-sm:full sm:right': {
    templateCarousel: 'max-sm:-mx-6 sm:-mr-6',
    carousel: 'max-sm:mx-break-out sm:mr-break-out',
  },
};

const templateCarouselClass = computed(() => carouselBleedMap[props.carouselBleed]?.templateCarousel || '');
const carouselClass = computed(() => carouselBleedMap[props.carouselBleed]?.carousel || '');

const breakpointsSettings = {
  // default (mobile first)
  0: {
    itemsToShow: props.carouselBreakPoints.default ?? 1,
  },
  // tailwind 'sm' (and up)
  640: {
    itemsToShow: props.carouselBreakPoints.sm ?? 1.25,
  },
  // md
  768: {
    itemsToShow: props.carouselBreakPoints.md ?? 1.25,
  },
  // lg
  1024: {
    itemsToShow: props.carouselBreakPoints.lg ?? 2.25,
  },
  // xl
  1280: {
    itemsToShow: props.carouselBreakPoints.xl ?? 2.25,
  },
  // 2xl
  1536: {
    itemsToShow: props.carouselBreakPoints['2xl'] ?? 3.25,
  },
};

const nextSlide = () => {
  myCarousel.value.next();
};

defineExpose({
  nextSlide,
});

// Check if image carousel only has one item visible
const allValuesAreOne = ref(false);
allValuesAreOne.value = Object.values(props.carouselBreakPoints).every((value) => value === 1);
const dynamicClass = computed(() => (allValuesAreOne.value ? 'condition-true' : 'condition-false'));

</script>
<style lang="scss" scoped>
:deep(.carousel.condition-false) {
  @media (min-width: 640px) {
    .carousel__slide {
      padding-left: 1rem;
      padding-right: 1rem;
      transition: padding-left 0.1s 0.1s, padding-right 0.1s 0.1s, margin-right 0.1s 0.1s; /* duration delay */
    }
    .carousel__slide--active {
      padding-left: 0;
      padding-right: 2rem;
      margin-right: -1rem;
    }
  }
}
:deep(.carousel) {
  .carousel__item {
    height: 100%;
    @media (max-width: 639px) {
      width: 100%;
    }
  }
  .carousel__prev,
  .carousel__next {
    background: none;
    color: rgb(31 41 55 / 0.5);
    top: initial;
    bottom: 0.9rem;
    transform: none;
    &:hover {
      color: rgb(31 41 55 / 1);
    }
  }
  .carousel__prev {
    left: 0;
  }
  .carousel__next {
    right: 0;
  }
  .carousel__pagination {
    margin-top: 0.5rem;
  }
  .carousel__pagination-button {
    height: 2px;
    display: block;
  }
}
</style>
